/**
 * CR142/CR309: Starburst Asset Picker for Theme Labs
 * 
 * A comprehensive picker component that allows admins to:
 * - Select a starburst from Asset Studio
 * - Clear selection to use default
 * - See resolution source (Selected/Default/Fallback)
 * - Navigate to Asset Studio or seed defaults if no assets exist
 * 
 * CR309: Now uses shared AssetPickerModal for consistency
 */

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getAssetImageSrc } from "@/lib/assets/getAppAssetPublicUrl";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  AlertTriangle, 
  ExternalLink, 
  Image, 
  Sparkles, 
  Star, 
  X 
} from "lucide-react";
import { toast } from "sonner";
import { useStarburstAssets } from "@/components/deals/ValueStarburstBadge";
import { useAppAssets } from "@/hooks/useAppAssets";
import { PLACEHOLDER_SVGS } from "@/lib/asset-studio/types";
import type { AppAsset } from "@/lib/asset-studio/types";
import { AssetPickerModal } from "./AssetPickerModal";

// ================================================================
// TYPES
// ================================================================

interface StarburstAssetPickerProps {
  /** Currently selected asset ID (from theme tokens) */
  selectedAssetId: string | null;
  /** Whether the starburst feature is enabled */
  starburstEnabled: boolean;
  /** Callback when user selects an asset */
  onSelectAsset: (assetId: string | null) => void;
}

type ResolutionSource = "selected" | "default" | "newest" | "fallback";

// ================================================================
// RESOLUTION HELPER
// ================================================================

function getResolutionInfo(
  selectedAssetId: string | null,
  assets: AppAsset[],
  defaultAsset: AppAsset | null
): { source: ResolutionSource; resolvedAsset: AppAsset | null } {
  // 1. Explicitly selected
  if (selectedAssetId) {
    const selected = assets.find(a => a.id === selectedAssetId);
    if (selected) {
      return { source: "selected", resolvedAsset: selected };
    }
  }
  
  // 2. Default tagged
  const tagged = assets.find(a => 
    a.tags?.some(t => t.toLowerCase() === "default")
  );
  if (tagged) {
    return { source: "default", resolvedAsset: tagged };
  }
  
  // 3. Most recently updated
  if (assets.length > 0) {
    const sorted = [...assets].sort((a, b) => 
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
    return { source: "newest", resolvedAsset: sorted[0] };
  }
  
  // 4. Fallback (no assets)
  return { source: "fallback", resolvedAsset: null };
}

const RESOLUTION_LABELS: Record<ResolutionSource, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
  selected: { label: "Selected", variant: "default" },
  default: { label: "Default Tag", variant: "secondary" },
  newest: { label: "Most Recent", variant: "outline" },
  fallback: { label: "Built-in Fallback", variant: "destructive" },
};

// ================================================================
// MAIN COMPONENT
// ================================================================

export function StarburstAssetPicker({ 
  selectedAssetId, 
  starburstEnabled,
  onSelectAsset,
}: StarburstAssetPickerProps) {
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  
  // Load assets
  const { 
    assets: starburstAssets, 
    hasAssets: hasStarburstAssets, 
    loading: starburstAssetsLoading,
    defaultAsset,
  } = useStarburstAssets();
  
  const { createAsset } = useAppAssets();
  
  // Resolution info
  const { source: resolutionSource, resolvedAsset } = useMemo(() => 
    getResolutionInfo(selectedAssetId, starburstAssets, defaultAsset),
    [selectedAssetId, starburstAssets, defaultAsset]
  );
  
  // Check if any asset has the "default" tag
  const hasDefaultTaggedAsset = useMemo(() => 
    starburstAssets.some(a => a.tags?.some(t => t.toLowerCase() === "default")),
    [starburstAssets]
  );
  
  // Handlers
  const handleClear = () => {
    onSelectAsset(null);
    toast.info("Selection cleared - using default starburst");
  };
  
  const handleSelectFromModal = (asset: { id: string; name: string } | null) => {
    if (asset) {
      onSelectAsset(asset.id);
      toast.success("Starburst selected");
    } else {
      onSelectAsset(null);
      toast.info("Selection cleared - using default starburst");
    }
  };
  
  const handleOpenAssetStudio = () => {
    navigate("/admin/asset-studio");
  };
  
  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      // Seed the Gold Starburst from PLACEHOLDER_SVGS (key is "starburst_gold")
      const goldStarburst = PLACEHOLDER_SVGS["starburst_gold"];
      if (goldStarburst) {
        const result = await createAsset({
          name: goldStarburst.name,
          type: goldStarburst.type,
          svg: goldStarburst.svg,
          tags: goldStarburst.tags,
          is_tileable: false,
        });
        if (result) {
          toast.success("Default starburst created with 'default' tag");
        }
      } else {
        // Fallback if key doesn't exist - create a basic gold starburst
        const result = await createAsset({
          name: "Gold Starburst",
          type: "starburst",
          svg: `<svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#FFD700"/>
      <stop offset="50%" stop-color="#FFC107"/>
      <stop offset="100%" stop-color="#FF9800"/>
    </linearGradient>
  </defs>
  <polygon points="60,5 71,35 105,35 78,57 89,91 60,70 31,91 42,57 15,35 49,35" fill="url(#goldGradient)"/>
</svg>`,
          tags: ["default", "starburst", "gold", "value"],
          is_tileable: false,
        });
        if (result) {
          toast.success("Default starburst created with 'default' tag");
        }
      }
    } catch (err) {
      console.error("[StarburstAssetPicker] Seed error:", err);
      toast.error("Failed to seed default starburst");
    } finally {
      setSeeding(false);
    }
  };
  
  // ================================================================
  // RENDER: Loading state
  // ================================================================
  
  if (starburstAssetsLoading) {
    return (
      <div className="space-y-3">
        <Label className="text-xs text-muted-foreground mb-2 block">Starburst Image</Label>
        <Skeleton className="h-20 w-full rounded-lg" />
      </div>
    );
  }
  
  // ================================================================
  // RENDER: Disabled state (starburst toggle is OFF)
  // ================================================================
  
  if (!starburstEnabled) {
    return (
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground mb-2 block flex items-center gap-1.5">
          <Image className="h-3 w-3" />
          Starburst Image
        </Label>
        <div className="p-3 rounded-lg bg-muted/30 border border-dashed">
          <p className="text-xs text-muted-foreground text-center">
            Enable Starburst to select an image.
          </p>
        </div>
      </div>
    );
  }
  
  // ================================================================
  // RENDER: No assets state
  // ================================================================
  
  if (!hasStarburstAssets) {
    return (
      <div className="space-y-3">
        <Label className="text-xs text-muted-foreground mb-2 block flex items-center gap-1.5">
          <Image className="h-3 w-3" />
          Starburst Image
        </Label>
        
        <Alert variant="destructive" className="py-3">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle className="text-sm">No Starburst Assets Found</AlertTitle>
          <AlertDescription className="text-xs mt-1">
            Add one in Asset Studio → New Asset → Type: Starburst.
          </AlertDescription>
        </Alert>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleOpenAssetStudio}
            className="flex-1 text-xs gap-1.5"
          >
            <ExternalLink className="h-3 w-3" />
            Open Asset Studio
          </Button>
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={handleSeedDefaults}
            disabled={seeding}
            className="flex-1 text-xs gap-1.5"
          >
            <Sparkles className="h-3 w-3" />
            {seeding ? "Creating..." : "Seed Defaults"}
          </Button>
        </div>
      </div>
    );
  }
  
  // ================================================================
  // RENDER: Normal state with selected asset row
  // ================================================================
  
  const resolutionInfo = RESOLUTION_LABELS[resolutionSource];
  
  return (
    <div className="space-y-3">
      <Label className="text-xs text-muted-foreground mb-2 block flex items-center gap-1.5">
        <Image className="h-3 w-3" />
        Starburst Image
      </Label>
      
      {/* No default tag warning */}
      {!hasDefaultTaggedAsset && (
        <Alert className="py-2 border-amber-500/30 bg-amber-500/10">
          <AlertTriangle className="h-3 w-3 text-amber-600" />
          <AlertDescription className="text-[11px] text-amber-700 dark:text-amber-300">
            No default starburst is set. Add tag <code className="bg-amber-500/20 px-1 rounded">default</code> to one asset.
          </AlertDescription>
        </Alert>
      )}
      
      {/* Selected Asset Row */}
      <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
        {/* CR201/CR282: Use img with resolved URL */}
        {resolvedAsset ? (
          <img 
            src={getAssetImageSrc(resolvedAsset) || ""}
            alt={resolvedAsset.name}
            className="w-10 h-10 flex-shrink-0 object-contain"
          />
        ) : (
          <div className="w-10 h-10 flex-shrink-0 bg-muted rounded flex items-center justify-center">
            <Star className="h-5 w-5 text-muted-foreground" />
          </div>
        )}
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate">
              {resolvedAsset ? resolvedAsset.name : "Built-in Fallback"}
            </span>
            <Badge variant="outline" className="text-[9px] px-1 py-0 flex-shrink-0">
              Starburst
            </Badge>
            {resolvedAsset?.tags?.some(t => t.toLowerCase() === "default") && (
              <Badge variant="secondary" className="text-[9px] px-1 py-0 flex-shrink-0">
                default
              </Badge>
            )}
          </div>
          
          {/* Using default hint */}
          {!selectedAssetId && resolvedAsset && (
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Using default starburst
            </p>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex gap-1.5 flex-shrink-0">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setPickerOpen(true)}
            className="h-7 text-xs px-2"
          >
            Change
          </Button>
          {selectedAssetId && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleClear}
              className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      
      {/* Resolution source hint */}
      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
        <span>Resolved from:</span>
        <Badge variant={resolutionInfo.variant} className="text-[9px] px-1.5 py-0">
          {resolutionInfo.label}
        </Badge>
      </div>
      
      {/* CR309: Use shared AssetPickerModal */}
      <AssetPickerModal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        filterType="starburst"
        assets={starburstAssets}
        selectedId={selectedAssetId}
        onSelect={handleSelectFromModal}
        title="Select Starburst Asset"
        allowClear={true}
      />
    </div>
  );
}
